import { CheckCircle2, Circle, Clock, CreditCard, Package, RefreshCw, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { OrderDetail } from '@/lib/api/orders';
import { DateDisplay } from '@/components/date-display';
import { formatMoney } from "@/lib/money";
import { cn } from "@/lib/utils";

interface OrderTimelineCardProps {
  order: OrderDetail;
}

interface TimelineEvent {
  id: string;
  type: string;
  title: string;
  description: string;
  timestamp: Date;
  icon: React.ReactNode;
  iconBg: string;
}

function getOrderEvents(order: OrderDetail): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  // Order created
  events.push({
    id: 'created',
    type: 'created',
    title: 'Order created',
    description: `Order ${order.id} was created`,
    timestamp: order.createdAt,
    icon: <Package className="h-4 w-4" />,
    iconBg: 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400',
  });

  // Payment captured
  if (order.paidAt && order.paidAmount) {
    events.push({
      id: 'paid',
      type: 'payment',
      title: 'Payment captured',
      description: `Payment of ${formatMoney(order.paidAmount, order.currency || 'USD')} was captured`,
      timestamp: order.paidAt,
      icon: <CreditCard className="h-4 w-4" />,
      iconBg: 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400',
    });
  }

  // Fulfilled
  if ((order as any).fulfilledAt) {
    events.push({
      id: 'fulfilled',
      type: 'fulfilled',
      title: 'Order fulfilled',
      description: 'Order was fulfilled and shipped',
      timestamp: (order as any).fulfilledAt,
      icon: <CheckCircle2 className="h-4 w-4" />,
      iconBg: 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-400',
    });
  }

  // Refunded
  if (order.refundedAmount && order.refundedAmount > 0) {
    events.push({
      id: 'refunded',
      type: 'refund',
      title: 'Order refunded',
      description: `Refund of ${formatMoney(order.refundedAmount, order.currency || 'USD')} was processed`,
      timestamp: order.updatedAt, // Use updatedAt as proxy
      icon: <RefreshCw className="h-4 w-4" />,
      iconBg: 'bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-400',
    });
  }

  // Cancelled
  if (order.status === 'cancelled' && order.cancelledAt) {
    events.push({
      id: 'cancelled',
      type: 'cancelled',
      title: 'Order cancelled',
      description: 'Order was cancelled',
      timestamp: order.cancelledAt,
      icon: <XCircle className="h-4 w-4" />,
      iconBg: 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400',
    });
  }

  // Sort by timestamp, most recent first
  return events.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
}

export function OrderTimelineCard({ order }: OrderTimelineCardProps) {
  const { t } = useTranslation('orders');
  const events = getOrderEvents(order);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('timeline')}</CardTitle>
        <CardDescription>{t('orderActivityHistory')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="relative space-y-6">
          {/* Vertical line */}
          <div className="bg-border absolute bottom-0 left-[15px] top-0 w-px" />

          {/* Events */}
          {events.map((event, index) => (
            <div key={event.id} className="relative flex gap-4">
              {/* Icon */}
              <div
                className={cn(
                  'relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                  event.iconBg,
                )}
              >
                {event.icon}
              </div>

              {/* Content */}
              <div className="flex-1 space-y-1 pt-0.5">
                <p className="font-medium">{event.title}</p>
                <p className="text-muted-foreground text-sm">{event.description}</p>
                <p className="text-muted-foreground text-xs"><DateDisplay value={event.timestamp} /></p>
              </div>
            </div>
          ))}

          {events.length === 0 && (
            <div className="text-muted-foreground py-8 text-center text-sm">{t('noActivityYet')}</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
